import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

/**
 * The operational reports keep their filters in the URL, like the financial ones: shareable and
 * back-button safe. An invalid value falls back to the default instead of breaking the page, and
 * defaults are applied by the pages so they never clutter the URL.
 */
const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)
const page = z.number().int().min(1).optional().catch(undefined)
const q = z.string().trim().max(200).optional().catch(undefined)
const uuid = z.uuid().optional().catch(undefined)

/** Page sizes of the paged reports (the API's default is 50, its maximum 200). */
export const REPORT_PAGE_SIZES = [50, 100, 200] as const
export const REPORT_DEFAULT_PAGE_SIZE = 50
type ReportPageSize = (typeof REPORT_PAGE_SIZES)[number]
const pageSize = z
  .number()
  .refine((n): n is ReportPageSize => (REPORT_PAGE_SIZES as readonly number[]).includes(n))
  .optional()
  .catch(undefined)

/** The sort keys a report's API accepts (anything else is dropped from the URL). */
function sortOf<const F extends string>(fields: readonly F[], defaultSort: F | `-${F}`) {
  const valid = (value: string): value is F | `-${F}` =>
    (fields as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
  return {
    valid,
    /** The API's order when none is given. */
    default: defaultSort,
    schema: z.string().refine(valid).optional().catch(undefined),
  }
}

/** A report's sort keys: which values are valid, and the API's default order. */
export interface ReportSort<F extends string> {
  valid: (value: string) => value is F | `-${F}`
  default: F | `-${F}`
}

export const SALES_BY_CUSTOMER_SORT = sortOf(['netSales', 'name', 'code', 'invoiceCount'], '-netSales')
export const SALES_BY_PRODUCT_SORT = sortOf(['netSales', 'grossProfit', 'quantity', 'name'], '-netSales')
export const PURCHASES_BY_SUPPLIER_SORT = sortOf(
  ['netPurchases', 'name', 'code', 'purchaseCount'],
  '-netPurchases',
)
export const PURCHASES_BY_PRODUCT_SORT = sortOf(['netPurchases', 'quantity', 'name'], '-netPurchases')

// ── sales and purchasing: a period, a search, a sort and a page ─────────────────────────────────

const pagedPeriod = { from: isoDate, to: isoDate, q, page, pageSize }

/** `customerId` narrows the report to one customer (the profile's "Net sales" figure links here). */
export const salesByCustomerSearchSchema = z.object({
  ...pagedPeriod,
  sort: SALES_BY_CUSTOMER_SORT.schema,
  customerId: uuid,
})
export type SalesByCustomerSearch = z.infer<typeof salesByCustomerSearchSchema>

export const salesByProductSearchSchema = z.object({ ...pagedPeriod, sort: SALES_BY_PRODUCT_SORT.schema })
export type SalesByProductSearch = z.infer<typeof salesByProductSearchSchema>

export const purchasesBySupplierSearchSchema = z.object({
  ...pagedPeriod,
  sort: PURCHASES_BY_SUPPLIER_SORT.schema,
})
export type PurchasesBySupplierSearch = z.infer<typeof purchasesBySupplierSearchSchema>

export const purchasesByProductSearchSchema = z.object({
  ...pagedPeriod,
  sort: PURCHASES_BY_PRODUCT_SORT.schema,
})
export type PurchasesByProductSearch = z.infer<typeof purchasesByProductSearchSchema>

export const SALES_GROUPINGS = ['month', 'day'] as const
export type SalesGrouping = (typeof SALES_GROUPINGS)[number]

/** Sales by period: the period (default this year) and the grouping (default month). */
export const salesByPeriodSearchSchema = z.object({
  from: isoDate,
  to: isoDate,
  groupBy: z.enum(SALES_GROUPINGS).optional().catch(undefined),
})
export type SalesByPeriodSearch = z.infer<typeof salesByPeriodSearchSchema>

/** Sales by salesperson (ADR-049): a period (default this month). */
export const salesBySalespersonSearchSchema = z.object({ from: isoDate, to: isoDate })
export type SalesBySalespersonSearch = z.infer<typeof salesBySalespersonSearchSchema>
/** Sales and collections by user (BR-RPT-008): the period only (default this month). */
export const salesByUserSearchSchema = z.object({ from: isoDate, to: isoDate })
export type SalesByUserSearch = z.infer<typeof salesByUserSearchSchema>

// ── inventory ────────────────────────────────────────────────────────────────

/** Stock valuation: as-of date (default today), warehouse, search and page. */
export const stockValuationSearchSchema = z.object({ asOf: isoDate, warehouseId: uuid, q, page, pageSize })
export type StockValuationSearch = z.infer<typeof stockValuationSearchSchema>

export const LOW_STOCK_GROUPINGS = ['total', 'warehouse'] as const
export type LowStockGrouping = (typeof LOW_STOCK_GROUPINGS)[number]

/** Low stock: all warehouses together (default) or per warehouse, one warehouse, search and page. */
export const lowStockSearchSchema = z.object({
  groupBy: z.enum(LOW_STOCK_GROUPINGS).optional().catch(undefined),
  warehouseId: uuid,
  q,
  page,
  pageSize,
})
export type LowStockSearch = z.infer<typeof lowStockSearchSchema>

// ── point of sale ────────────────────────────────────────────────────────────

/** Till sales summary: the period (default this month), a till, a cashier and the page. */
export const posSummarySearchSchema = z.object({
  from: isoDate,
  to: isoDate,
  terminalId: uuid,
  userId: uuid,
  page,
  pageSize,
})
export type PosSummarySearch = z.infer<typeof posSummarySearchSchema>

// ── home ─────────────────────────────────────────────────────────────────────

/** The home dashboard's figures date (default today in Muscat) and its top-sellers period. */
export const homeSearchSchema = z.object({
  asOf: isoDate,
  /** The period of the top customers and products (default: this month). */
  top: z.enum(['month', 'year']).optional().catch(undefined),
})
export type HomeSearch = z.infer<typeof homeSearchSchema>
